/**
 * Frontend TypeScript types for ViewerNfcScanContext API response
 *
 * Matches backend response types from:
 * cdk-anyw3ar-backend/functions/webapp/viewer/nfc/context/domain/types/ScanContextResponses.ts
 *
 * These types represent the different states an NFC scan can return when a viewer
 * (authenticated user or anonymous) scans an NFC-enabled ANYW3AR product.
 */

/**
 * Product information returned in scan context
 */
export interface ProductInfo {
  productId: string;
  title: string;
  imageUrl: string;
  productType: string;
  description?: string;
  partnerId?: string;
  editionSize?: number;
  itemNumber?: number;
}

/**
 * Owner information for claimed items
 *
 * @property username - Owner's display name
 * @property avatar - Optional avatar URL
 * @property userId - Always present (enables Trophy Room redirect for owned items)
 */
export interface OwnerInfo {
  username: string;
  avatar?: string;
  userId: string;
}

/**
 * Response for invalid NFC signature
 *
 * Indicates the scanned NFC ID has an invalid cryptographic signature,
 * suggesting a counterfeit or tampered product.
 */
export interface InvalidSignatureContext {
  isAuthentic: false;
  error: string;
}

/**
 * Response for stolen or lost items
 *
 * Returned when the item has been reported as stolen/lost by the owner.
 * Viewer is shown owner information and report timestamp.
 */
export interface StolenItemContext {
  type: "stolen" | "lost";
  isAuthentic: true;
  nfcId: string;
  productInfo: ProductInfo;
  reportedAt: string;
  ownerInfo: OwnerInfo;
}

/**
 * Response for claimed items (viewer perspective)
 *
 * Returned when an item has been claimed by an owner.
 * Owner information visibility depends on owner's privacy settings.
 *
 * Story 3.7.b: partnerLink added for partner redirect button
 * Story 3.8.b: eventUrl, eventName, partnerLogo added for partner event animation
 */
export interface ClaimedItemContextViewer {
  type: "claimed";
  isAuthentic: true;
  nfcId: string;
  productInfo: ProductInfo;
  visibility: "public" | "private";
  ownerInfo: OwnerInfo;
  partnerLink?: string; // Story 3.7.b: Partner permanent redirect URL
  eventUrl?: string;    // Story 3.8.b: Temporary event redirect URL (PRIORITY)
  eventName?: string;   // Story 3.8.b: Event display name
  partnerLogo?: string; // Story 3.8.b: Partner logo URL (denormalized)
}

/**
 * Response for unclaimed items
 *
 * Returned when an item has not yet been claimed by any owner.
 * Viewer is shown product information only.
 */
export interface UnclaimedItemContextViewer {
  type: "unclaimed";
  isAuthentic: true;
  nfcId: string;
  productInfo: ProductInfo;
}

/**
 * Union type for all possible viewer NFC scan context responses
 */
export type ViewerNfcScanContextResponse =
  | InvalidSignatureContext
  | StolenItemContext
  | ClaimedItemContextViewer
  | UnclaimedItemContextViewer;

/**
 * Type guard: Check if response indicates invalid signature
 */
export function isInvalidSignature(
  context: ViewerNfcScanContextResponse
): context is InvalidSignatureContext {
  return !context.isAuthentic;
}

/**
 * Type guard: Check if response indicates stolen/lost item
 */
export function isStolenItem(
  context: ViewerNfcScanContextResponse
): context is StolenItemContext {
  return (
    context.isAuthentic &&
    (context.type === "stolen" || context.type === "lost")
  );
}

/**
 * Type guard: Check if response indicates claimed item
 */
export function isClaimedItem(
  context: ViewerNfcScanContextResponse
): context is ClaimedItemContextViewer {
  return context.isAuthentic && context.type === "claimed";
}

/**
 * Type guard: Check if response indicates unclaimed item
 */
export function isUnclaimedItem(
  context: ViewerNfcScanContextResponse
): context is UnclaimedItemContextViewer {
  return context.isAuthentic && context.type === "unclaimed";
}
